import React from 'react'
import appConfig from '../../../config'
import { Feather } from '../../../components/icon/vectorIcons'
// A real anchor, for the same reason as OpenInMi6Link: middle-click, cmd-click
// and the browser's own new-tab handling come for free.
// eslint-disable-next-line no-restricted-imports
import styledWeb from 'styled-components'

import useTranslation from '../../../hooks/useTranslation'
import { IContentFeedItem } from '../queries/getContentFeeds'
import { isNotYetVisible } from './ContentItemSummary'

const MEMBER_WEB_PROTOCOL = appConfig.MEMBER_WEB_PROTOCOL
const MEMBER_WEB_URL = appConfig.MEMBER_WEB_URL

const ICON_SIZE = 18

/** Matches OpenInMi6Link, glyph included: both open somewhere else in a new tab. */
const ICON_COLOR = '#21201B'

/**
 * The member app's routes for the three item types, mirroring its Expo Router
 * files (media/[id], document/[id], library/[id]). Doclets live under
 * /library there, not /doclet. Plain strings, so a route rename on that side
 * only breaks the link, never this build -- same trade-off as mi6PathForItem.
 */
const MEMBER_APP_SEGMENT: Record<IContentFeedItem['__typename'], string> = {
  Media: 'media',
  Document: 'document',
  Doclet: 'library'
}

export const memberAppUrlForItem = (item: IContentFeedItem): string =>
  `${MEMBER_WEB_PROTOCOL}://${MEMBER_WEB_URL}/${
    MEMBER_APP_SEGMENT[item.__typename]
  }/${item.id}`

const iconBox = `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
`

const Anchor = styledWeb.a`
  ${iconBox}
  text-decoration: none;
`

/** Keeps the column width when there is nothing to open, so rows line up. */
const Inert = styledWeb.span`
  ${iconBox}
  opacity: 0.35;
`

interface OpenInMemberAppLinkProps {
  /** Null when the record has been deleted. */
  item: IContentFeedItem | null
}

/**
 * Opens the item as members see it, in a new tab, so a curator can check what
 * they are about to feature without leaving the picker.
 *
 * Inert rather than hidden for items members cannot reach yet: the member app
 * has nothing to show for an unpublished or scheduled item, and the tooltip
 * says why instead of sending the curator to an empty page.
 */
export const OpenInMemberAppLink = ({ item }: OpenInMemberAppLinkProps) => {
  const { t } = useTranslation()

  if (!item) {
    return (
      <Inert title={t('contentCuration:select:openInMemberAppUnavailable')}>
        <Feather name="external-link" size={ICON_SIZE} color={ICON_COLOR} />
      </Inert>
    )
  }

  if (isNotYetVisible(item)) {
    return (
      <Inert title={t('contentCuration:hero:notVisible')}>
        <Feather name="external-link" size={ICON_SIZE} color={ICON_COLOR} />
      </Inert>
    )
  }

  return (
    <Anchor
      href={memberAppUrlForItem(item)}
      target="_blank"
      rel="noopener noreferrer"
      title={t('contentCuration:select:openInMemberApp')}
      aria-label={t('contentCuration:select:openInMemberApp')}
      // The picker row is itself pressable (it toggles selection). Without this
      // the click reaches the row too, and opening a preview also ticks or
      // unticks the item.
      onClick={event => event.stopPropagation()}
    >
      <Feather name="external-link" size={ICON_SIZE} color={ICON_COLOR} />
    </Anchor>
  )
}

export default OpenInMemberAppLink
